// A - the header. Two timestamps, always, side by side (spec section 2): when the DATA was evaluated and when
// THIS PAGE was built. They are different questions; conflating them is how a frozen page passes for a live one.
// The state store's own check is summarised here too, so "can this page be trusted" is on the face of the page
// in every case, not only at its foot (section E stays where the spec's reading order puts it).
import { Component, type ReactNode } from "react";
import type { HealthResponse } from "./types";
import { POLL_MS, clip, fmtAgeH, fmtAgo, fmtAgoMs, fmtUtc, isTime, look, shortSha, type BuildStamp, type Look } from "./present";
import { StateWord } from "./Tiles";

const commitText = (c: string) => (/^[0-9a-f]{7,40}$/i.test(c) ? c.slice(0, 7).toLowerCase() : clip(c, 40));

/** The header sits OUTSIDE the body's render guard (it must survive a body crash), so anything in it that reads
 *  the answer is contained: a malformed answer degrades this one line to UNKNOWN, never the whole page to blank. */
class Contained extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    const lk: Look = { kind: "unknown", word: "UNKNOWN", contradiction: null };
    return (
      <div className="ph-time" data-testid="header-store">
        <dt>state store</dt>
        <dd>
          <StateWord lk={lk} /> the answer's store section could not be drawn
        </dd>
      </div>
    );
  }
}

function StoreSummary({ data }: { data: HealthResponse }) {
  const s = data.store;
  const lk = look(s.self);
  const named = typeof s.self.value === "string" ? s.self.value : null;
  const entry = named !== null ? s.entries.find(e => e !== null && typeof e === "object" && e.monitor === named) : undefined;
  const entryAge = entry ? (entry.last_write_utc ? fmtAgeH(entry.age_h) : "never written") : "not in the entry list";
  return (
    <div className="ph-time" data-testid="header-store">
      <dt>state store</dt>
      <dd>
        <span className={`ph-chip ph-k-${lk.kind}`}>
          <StateWord lk={lk} />
        </span>{" "}
        {!s.read_ok ? (
          "could not be read"
        ) : named !== null ? (
          <>
            oldest entry <code>{named}</code> · {entryAge}
          </>
        ) : (
          "its check names no oldest entry"
        )}
      </dd>
      <dd className="ph-sub">its own check · details in the STATE STORE line below the headline</dd>
    </div>
  );
}

export function HeaderA({
  data,
  asOfNote,
  stamp,
  now,
  lastSuccessMs
}: {
  data: HealthResponse | null;
  /** What "as of" says when no answer is on the page: never an answer yet, or one that has been withdrawn. */
  asOfNote: string;
  stamp: BuildStamp;
  now: number;
  lastSuccessMs: number | null;
}) {
  const built = isTime(stamp.utc) ? stamp.utc : null;
  return (
    <header className="ph-a">
      <h1 className="ph-title">PLATFORM HEALTH</h1>
      <dl className="ph-times">
        <div className="ph-time" data-testid="as-of">
          <dt>as of</dt>
          <dd>
            {data ? (
              <>
                {fmtUtc(data.evaluated_at_utc)} <span className="ph-age">· {fmtAgo(data.evaluated_at_utc, now)}</span>
              </>
            ) : (
              asOfNote
            )}
          </dd>
          <dd className="ph-sub">when the data was evaluated</dd>
        </div>
        <div className="ph-time" data-testid="page-built">
          <dt>page built</dt>
          <dd>
            {built ? (
              <>
                {fmtUtc(built)} · <code>{shortSha(stamp.sha)}</code> <span className="ph-age">· {fmtAgo(built, now)}</span>
              </>
            ) : (
              <>
                UNKNOWN - this page carries no build stamp · <code>{shortSha(stamp.sha)}</code>
              </>
            )}
          </dd>
          <dd className="ph-sub">when this page itself was built</dd>
        </div>
        <div className="ph-time" data-testid="last-fetch">
          <dt>last successful fetch</dt>
          <dd>
            {lastSuccessMs !== null ? (
              <>
                {fmtUtc(new Date(lastSuccessMs).toISOString())} <span className="ph-age">· {fmtAgoMs(now - lastSuccessMs)}</span>
              </>
            ) : (
              "never"
            )}
          </dd>
          <dd className="ph-sub">fetched and drawn · refreshes every {POLL_MS / 60_000} minutes</dd>
        </div>
        {data ? (
          <Contained key={data.evaluated_at_utc}>
            <StoreSummary data={data} />
          </Contained>
        ) : null}
      </dl>
      {data ? (
        <p className="ph-model">
          topology model <code>{String(data.model.source_path)}</code> @ <code>{commitText(String(data.model.source_commit))}</code> · generated{" "}
          {String(data.model.generated)} · {String(data.model.status)}
        </p>
      ) : null}
    </header>
  );
}
